import React, { useCallback, useEffect, useState } from "react";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../lib/api.ts";
// currency utilities
import { useCurrencyUtils } from "../../utils/currencyUtils";
import Pagination from "../common/Pagination";
import { FaCheckCircle, FaWallet, FaClock, FaEye } from "react-icons/fa";
import { useToast } from "../../context/ToastContext";;
const ProfilePayment = () => {
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();
  const [paymentData, setPaymentData] = useState([]);

  const { showSuccess, showError } = useToast();
  
  // New state for withdrawal flow
  const [showConfirm, setShowConfirm] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [isLoading, setIsLoading] = useState(true);

  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [activeReceiptUrl, setActiveReceiptUrl] = useState("");

  const handleViewReceipt = (url) => {
    if (!url) return;
    const isPdf = url.toLowerCase().includes(".pdf");
    if (isPdf) {
      window.open(url, "_blank");
    } else {
      setActiveReceiptUrl(url);
      setShowReceiptModal(true);
    }
  };

  const handleWithdrawClick = (transaction) => {
    if (!transaction?.id) return;
    if (transaction.paymentStatus !== "Paid") return;
    if (getWithdrawStatus(transaction) !== "none") return;

    setSelectedTransactionId(transaction.id);
    setShowConfirm(true);
  };

  const getWithdrawStatus = (transaction) => {
    if (transaction?.withdrawStatus) return transaction.withdrawStatus;
    if (transaction?.withdrawRequested) return "requested";
    return "none";
  };

  const fetchPaymentData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(
        `/sellers/commissions?page=${pagination.currentPage}&limit=${pagination.itemsPerPage}`,
      );

      if (response.data && response.data.data) {
        setPaymentData(response.data.data);
        setPagination({
          currentPage: response.data.pagination?.currentPage || 1,
          totalPages: response.data.pagination?.totalPages || 1,
          totalItems: response.data.pagination?.totalItems || 0,
          itemsPerPage: response.data.pagination?.itemsPerPage || 10,
        });
      }
    } catch (error) {
      console.error("Error fetching payment data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [pagination.currentPage, pagination.itemsPerPage]);

  const handleConfirmWithdraw = async () => {
    if (isWithdrawing || !selectedTransactionId) return;
    
    try {
      setIsWithdrawing(true);
      
      // 1. THIS IS THE MISSING PIECE: You must define 'response' here
      const response = await api.post(`/sellerwithdrawalinvoices/withdrawrequest`, { 
        transactionId: selectedTransactionId 
      });

      // 2. Now 'response' is defined and can be safely checked
      if (response.data && response.data.success === false) {
         throw new Error(response.data.message || "Withdraw request failed");
      }
      
      showSuccess(translate("sellerPayment.messages.withdrawRequestSubmitted") || "Withdraw request submitted.");
      
      // Update the local state instantly so the button turns grey without refreshing
      setPaymentData((prevData) => 
        prevData.map((item) => 
          item.id === selectedTransactionId 
            ? { ...item, withdrawStatus: "requested", withdrawRequested: 1 } 
            : item
        )
      );
      
    } catch (error) {
      console.error("Withdrawal error:", error);
      // Notice how here we use 'error.response' instead of 'response'
      const errorMsg = error.response?.data?.message || error.message || translate("sellerPayment.messages.withdrawRequestFailed") || "Withdraw request failed.";
      showError(errorMsg);
    } finally {
      setIsWithdrawing(false);
      setShowConfirm(false);
      setSelectedTransactionId(null);
    }
  };
  
  useEffect(() => {
    fetchPaymentData();
    window.addEventListener("focus", fetchPaymentData);

    const refreshTimer = setInterval(() => {
      fetchPaymentData();
    }, 30000);

    return () => {
      window.removeEventListener("focus", fetchPaymentData);
      clearInterval(refreshTimer);
    };
  }, [fetchPaymentData]);

  if (isLoading) {
    return (
      <div
        className=" rounded-lg shadow-md"
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        {/* Header */}
        <div className="bg-[#fcfcfc1f] text-white font-medium rounded-t-[16px]">
          <div className="p-[20px] text-lg rounded-t-[12px] font-semibold text-white bg-primary-soft">
            <h2 className="text-base sm:text-lg font-semibold">
              {translate("sellerPayment.heading")}
            </h2>
          </div>
        </div>
        <div className="px-3 py-6">
          <div className="animate-pulse">
            <div className="h-6 bg-page rounded w-1/3 mb-6"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-card rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-card rounded-lg shadow-md"
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Header */}

      <div className="bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft gap-4 min-h-[120px] lg:min-h-[72px]">
          {/* Left Side Grouping */}
          <div className="flex flex-col justify-center">
            <h2 className="text-white text-base sm:text-lg font-semibold whitespace-nowrap">
              {translate("sellerPayment.heading")}
            </h2>
          </div>

          {/* Right Side Grouping (Kept for height and alignment consistency) */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3 ms-auto lg:ms-0 w-full lg:w-auto"></div>
        </div>
      </div>

      <div className="px-3 py-6">
        {/* Mobile Card View */}
        <div className="block lg:hidden space-y-4">
          {paymentData.map((transaction) => {
            const withdrawStatus = getWithdrawStatus(transaction);
            const withdrawRequested = withdrawStatus === "requested";
            const withdrawApproved = withdrawStatus === "approved";
            const canWithdraw =
              transaction.paymentStatus === "Paid" && withdrawStatus === "none";
            const showViewReceipt = withdrawApproved && !!transaction.withdrawReceiptUrl;
            const isButtonDisabled = isWithdrawing || (!canWithdraw && !showViewReceipt);

            let buttonText = translate("sellerPayment.buttons.withdraw") || "Withdraw";
            if (showViewReceipt) {
              buttonText = translate("sellerPayment.buttons.viewReceipt") || "View Receipt";
            } else if (withdrawApproved) {
              buttonText = translate("sellerPayment.buttons.withdrawn") || "Withdrawn";
            } else if (withdrawRequested) {
              buttonText = translate("sellerPayment.buttons.requested") || "Requested";
            }

            const handleButtonClick = () => {
              if (showViewReceipt) {
                handleViewReceipt(transaction.withdrawReceiptUrl);
              } else if (canWithdraw) {
                handleWithdrawClick(transaction);
              }
            };

            return (
            <div
              key={transaction.id}
              className="rounded-lg shadow-sm border border-gray-200 p-4"
            >
              <div className="space-y-3">
                {/* Vehicle Info */}
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">
                      {transaction.vehicle?.name || "-"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {transaction.vehicle?.lotNumber || "-"}
                    </div>
                  </div>
                  {/* <button
                    className={`px-3 py-1 bg-green-600 text-white rounded-md text-xs whitespace-nowrap ${
                      isRTL ? "mr-2" : "ml-2"
                    }`}
                  > */}
                  <div className="flex flex-col gap-2 align-items-end">
                    <button
                      disabled={isButtonDisabled}
                      onClick={handleButtonClick}
                      className={`px-3 py-1 rounded-md text-xs whitespace-nowrap ${
                        isRTL ? "mr-2" : "ml-2"
                      } ${
                        canWithdraw || showViewReceipt
                          ? "bg-green-600 text-white hover:bg-green-700 cursor-pointer active:scale-95 transition-all"
                          : withdrawApproved
                            ? "bg-green-600 text-white cursor-not-allowed" // Green success for Withdrawn
                            : withdrawRequested
                              ? "bg-gray-400 text-white cursor-not-allowed" // Gray for Requested
                              : "bg-gray-300 text-gray-500 cursor-not-allowed" // Gray for Unpaid
                      }`}
                    >
                      {buttonText}
                    </button>
                  </div>
                </div>

                {/* Financial Details */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-gray-500">
                      {translate("sellerPayment.labels.soldPrice")}
                    </div>
                    <div className="font-semibold text-green-600">
                      {displayPriceWithSymbol(
                        convertPrice(transaction.soldPrice),
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">
                      {translate("sellerPayment.labels.commission")}
                    </div>
                    <div className="font-semibold text-blue-600">
                      {displayPriceWithSymbol(
                        convertPrice(transaction.commission),
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">
                      {translate("sellerPayment.labels.vat5")}
                    </div>
                    <div className="font-semibold text-orange-600">
                      {displayPriceWithSymbol(
                        convertPrice(transaction.vatAmount),
                      )}
                    </div>
                  </div>
                  {/* wednesday fix  */}

                  <div>
                    <div className="text-gray-500">
                      {translate("sellerPayment.labels.listingFee")}
                    </div>
                    <div className="font-semibold text-white">
                      {displayPriceWithSymbol(
                        convertPrice(Number(transaction.listingFee) || 0),
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-gray-500">
                      {translate("sellerPayment.labels.payableAmount")}
                    </div>
                    <div className="font-semibold text-white">
                      {displayPriceWithSymbol(
                        convertPrice(
                          (Number(transaction.totalCommission) || 0) +
                            (Number(transaction.listingFee) || 0),
                        ),
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-gray-500">
                      {translate("sellerPayment.labels.withdrawable")}
                    </div>
                    <div className="font-semibold text-white">
                      {displayPriceWithSymbol(
                        convertPrice(
                          (Number(transaction.soldPrice) || 0) -
                            (Number(transaction.totalCommission) || 0) -
                            (Number(transaction.listingFee) || 0),
                        ),
                      )}
                    </div>
                  </div>
                </div>

                {/*===================e================== */}

                {/* Sale Date */}
                <div className="text-xs text-gray-500">
                  {translate("sellerPayment.labels.saleDate")}:{" "}
                  {new Date(transaction.createdAt).toLocaleDateString(
                    language === "ar" ? "ar-AE" : "en-AE",
                    {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    },
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-hidden rounded-lg shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-page">
              <tr>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.vehicle")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.soldPrice")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.commission")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.vat5")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.listingFee")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.payable")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.withdrawableAmount")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.saleDate")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-white uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.action")}
                </th>
              </tr>
            </thead>
            <tbody className="">
              {paymentData.map((transaction) => {
                const withdrawStatus = getWithdrawStatus(transaction);
                const withdrawRequested = withdrawStatus === "requested";
                const withdrawApproved = withdrawStatus === "approved";
                const canWithdraw =
                  transaction.paymentStatus === "Paid" && withdrawStatus === "none";
                const showViewReceipt = withdrawApproved && !!transaction.withdrawReceiptUrl;
                const isButtonDisabled = isWithdrawing || (!canWithdraw && !showViewReceipt);

                let buttonText = translate("sellerPayment.buttons.withdraw") || "Withdraw";
                if (showViewReceipt) {
                  buttonText = translate("sellerPayment.buttons.viewReceipt") || "View Receipt";
                } else if (withdrawApproved) {
                  buttonText = translate("sellerPayment.buttons.withdrawn") || "Withdrawn";
                } else if (withdrawRequested) {
                  buttonText = translate("sellerPayment.buttons.requested") || "Requested";
                }

                const handleButtonClick = () => {
                  if (showViewReceipt) {
                    handleViewReceipt(transaction.withdrawReceiptUrl);
                  } else if (canWithdraw) {
                    handleWithdrawClick(transaction);
                  }
                };

                return (
                <tr key={transaction.id} className="border-b border-gray-200">
                  <td className="px-6 py-4 ">
                    <div>
                      <div className="text-sm font-medium text-white">
                        {transaction.vehicle?.name || "-"}
                      </div>
                      <div className="text-sm text-description">
                        {transaction.vehicle?.lotNumber || "-"}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-white">
                    {displayPriceWithSymbol(
                      convertPrice(transaction.soldPrice),
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-white">
                    {displayPriceWithSymbol(
                      convertPrice(transaction.commission),
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-white">
                    {displayPriceWithSymbol(
                      convertPrice(transaction.vatAmount),
                    )}
                  </td>
                  {/* ====wednesday fix  */}
                  <td className="px-6 py-4 text-sm text-white">
                    {displayPriceWithSymbol(
                      convertPrice(transaction.listingFee),
                    )}
                  </td>

                  <td className="px-6 py-4 text-sm text-white">
                    {displayPriceWithSymbol(
                      convertPrice(
                        (Number(transaction.totalCommission) || 0) +
                          (Number(transaction.listingFee) || 0),
                      ),
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-white">
                    {displayPriceWithSymbol(
                      convertPrice(
                        (Number(transaction.soldPrice) || 0) -
                          (Number(transaction.totalCommission) || 0) -
                          (Number(transaction.listingFee) || 0),
                      ),
                    )}
                  </td>

                  {/* //===================e================== // */}
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-description">
                    {new Date(transaction.createdAt).toLocaleDateString(
                      language === "ar" ? "ar-AE" : "en-AE",
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      },
                    )}
                    <br />
                    {new Date(transaction.createdAt).toLocaleTimeString(
                      language === "ar" ? "ar-AE" : "en-AE",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-white">
                    <div className="flex items-center gap-3">
                      <div
                        className={`relative h-10 w-10 ${isRTL ? "mr-2" : "ml-2"}`}
                        style={{ minWidth: "40px" }}
                      >
                        <button
                          disabled={isButtonDisabled}
                          onClick={handleButtonClick}
                          className={`absolute ${isRTL ? "left-0" : "right-0"} top-0 h-10 w-10 hover:w-36 flex items-center border rounded-lg transition-all duration-300 ease-in-out shadow-sm overflow-hidden z-10 hover:z-30 group
          ${canWithdraw || showViewReceipt
                              ? "bg-green-600 border-green-500 text-white hover:bg-green-700 cursor-pointer active:scale-95"
                              : withdrawApproved
                                ? "bg-green-600 border-green-600 text-white cursor-not-allowed" // Green success for Withdrawn
                                : withdrawRequested
                                  ? "bg-gray-400 border-gray-400 text-white cursor-not-allowed" // Gray for Requested
                                  : "bg-gray-300 border-gray-300 text-gray-500 cursor-not-allowed" // Gray for Unpaid
                            }`}
                        >
                          <div className="flex items-center h-full w-full">
                           {/* Icon Container: Centered in the square */}
                            <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                              {showViewReceipt ? (
                                <FaEye size={18} />
                              ) : withdrawApproved ? (
                                <FaCheckCircle size={18} /> // Shows a check for completed
                              ) : withdrawRequested ? (
                                <FaClock size={18} /> // Shows a clock for pending
                              ) : (
                                <FaWallet size={18} /> // Shows a wallet for ready to withdraw
                              )}
                            </div>

                            {/* Text: Reveals on hover */}
                            <span className="pr-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs font-bold whitespace-nowrap">
                              {buttonText}
                            </span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {paymentData.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <div className="text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-white">
                {translate("sellerPayment.messages.noPaymentsFound")}
              </h3>
              <p className="mt-1 text-sm text-description">
                {translate("sellerPayment.messages.noPaymentsAvailable")}
              </p>
            </div>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-3 lg:px-6 py-4 border-t border-gray-200">
            <Pagination
              totalPages={pagination.totalPages}
              currentPage={pagination.currentPage}
              setCurrentPage={(page) => {
                // You can implement page change logic here
                setPagination({
                  ...pagination,
                  currentPage: page,
                });
                console.log("Page changed to:", page);
              }}
            />
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <>
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000 }}
            onClick={() => !isWithdrawing && setShowConfirm(false)}
          />
          <div
            style={{
              position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 10001, pointerEvents: "none",
            }}
          >
            <div
              className="bg-white rounded-lg shadow-2xl border p-6"
              style={{ pointerEvents: "auto", width: "90%", maxWidth: "400px" }}
              dir={isRTL ? "rtl" : "ltr"}
            >
              <div className="mb-5">
                <h6 className="fw-bold text-dark m-0">
                  {translate("sellerPayment.messages.confirmWithdraw") || "Are you sure you want to request a withdrawal for this payment?"}
                </h6>
              </div>

              <div
                style={{
                  display: "flex", gap: "12px", width: "100%",
                  justifyContent: isRTL ? "flex-start" : "flex-end",
                }}
              >
                <button
                  onClick={() => setShowConfirm(false)}
                  disabled={isWithdrawing}
                  className="btn btn-light border px-4 py-2 text-sm"
                  style={{ borderRadius: "6px" }}
                >
                  {translate("sellerPayment.buttons.cancel") || "Cancel"}
                </button>

                <button
                  onClick={handleConfirmWithdraw}
                  disabled={isWithdrawing}
                  className={`btn px-4 py-2 text-sm font-semibold`}
                  style={{
                    backgroundColor: "transparent",
                    color: isWithdrawing ? "#6c757d" : "var(--primary-color, #0d6efd)",
                    border: `1px solid ${isWithdrawing ? "#dee2e6" : "var(--primary-color, #0d6efd)"}`,
                    borderRadius: "6px",
                    minWidth: "100px",
                  }}
                >
                  {isWithdrawing
                    ? "..."
                    : (translate("sellerPayment.buttons.confirm") || "Confirm")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {showReceiptModal && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10000,
            }}
            onClick={() => setShowReceiptModal(false)}
          />

          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10001,
              pointerEvents: "none",
            }}
          >
            <div
              className="bg-white rounded-lg shadow-2xl border p-5"
              style={{
                pointerEvents: "auto",
                width: "90%",
                maxWidth: "600px",
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
              }}
              dir={isRTL ? "rtl" : "ltr"}
            >
              <div className="flex justify-between items-center mb-4 border-b pb-2">
                <h6 className="font-bold text-dark m-0">
                  Transaction Receipt
                </h6>
                <button
                  type="button"
                  onClick={() => setShowReceiptModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "20px",
                    cursor: "pointer",
                    color: "#000",
                    fontWeight: "bold",
                  }}
                >
                  &times;
                </button>
              </div>

              <div className="flex-1 overflow-auto text-center py-2" style={{ maxHeight: "70vh" }}>
                <img
                  src={activeReceiptUrl}
                  className="img-fluid rounded mx-auto"
                  alt="Receipt Preview"
                  style={{ maxWidth: "100%", maxHeight: "60vh", objectFit: "contain" }}
                />
              </div>

              <div className="text-end border-t pt-3 mt-2">
                <button
                  type="button"
                  onClick={() => setShowReceiptModal(false)}
                  className="btn btn-secondary px-4 py-1.5 text-xs font-semibold"
                  style={{ borderRadius: "4px" }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProfilePayment;
